import { useState } from "react";

export type FAQItem = { q: string; a: string };

const FAQ_ITEMS: FAQItem[] = [
  {
    q: "Is My Couponbook the best coupon app in Canada for local deals?",
    a: "Yes, we believe so! We built My Couponbook specifically to connect Canadians with the best independent businesses in their own communities. While other apps focus on massive corporate chains or force you to buy generic online vouchers, our platform pushes fresh, high-value deals directly from local restaurants, shops, and services right to your personalized feed.",
  },
  {
    q: "What are the best ways to save money in Canada?",
    a: "Finding local deals and discounts allows you to do more while saving money. My Couponbook helps you do exactly that by instantly connecting you to local businesses. Because businesses post new deals daily, you can find high-value offers—like BOGO (Buy-One-Get-One) deals, percentage discounts, or time-specific flash sales—on things you were already going to buy. We make it effortless to discover hidden gems in your city while keeping more money in your pocket.",
  },
  {
    q: "Is My Couponbook free to use?",
    a: "Yes! You can download the app and create a Free account instantly. With a Free account, you can follow your favourite local spots, browse the daily feed, redeem up to 10 deals every single month, and start collecting our exclusive Coupon Points right away.",
  },
  {
    q: "How do the Coupon Points work?",
    a: "Think of Coupon Points as your reward for supporting local businesses. When you redeem specific deals, or refer friends to the app, you earn points. As your points accumulate, you build a digital wallet of savings. The more you use the app to support your community, the faster your points grow.",
  },
  {
    q: "How do I spend the Coupon Points I collect?",
    a: "This is where the massive value kicks in. To spend your collected Coupon Points, you simply upgrade to a Premium Membership. Once upgraded, your points unlock, and you can use them to completely cover the cost of specific coupons within the app—meaning your loyalty literally pays for your next local experience.",
  },
  {
    q: "How much is a Premium Membership, and is it worth it?",
    a: "A Premium Membership is just $12 for an entire year. When you upgrade, you immediately unlock all your collected Coupon Points, gain access to exclusive Premium-only deals, and get unlimited coupon redemptions every month. When you compare the $12 annual fee against the $100s of dollars you stand to save over a year of unlimited, high-value local deals, the Premium Membership easily pays for itself.",
  },
  {
    q: "Can I use My Couponbook anywhere in Canada?",
    a: "Absolutely. We are a Canada-wide platform. Whether you are grabbing a coffee in Halifax, discovering a new restaurant in Toronto, or booking a service in Vancouver, as long as there are local businesses on the platform in your area, you can pull up the app and start saving immediately.",
  },
  {
    q: "Do I have to pay the app for the deal, like other voucher sites?",
    a: "No, and this is a huge difference. You never pay My Couponbook for the goods or services. You simply activate the coupon in the app, show it to the business when you arrive, and pay the business directly at their register. This keeps the money in your community where it belongs. (The only exception is when Premium members choose to \"Pay with Points\" directly in the app!).",
  },
];

type FAQSectionProps = {
  items?: FAQItem[];
  // Lets a page match its own section width; defaults to the landing page layout.
  containerClassName?: string;
};

export const FAQSection = ({
  items = FAQ_ITEMS,
  containerClassName = "w-full max-w-[1700px] px-6",
}: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number>(0);

  return (
    <section className="w-full flex justify-center">
      <div className={containerClassName}>

        {/* LABEL */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-[10px] h-[10px] rounded-full bg-red-600" />
          <span className="text-gray-600 text-[14px] font-semibold uppercase tracking-[1px]">
            FAQ
          </span>
        </div>

        {/* HEADING */}
        <h2 className="text-[28px] lg:text-[40px] font-medium text-neutral-900 text-center mb-10 lg:mb-14">
          Everything you need to know
        </h2>

        {/* ACCORDION */}
        <div className="space-y-4">
          {items.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={item.q}
                className={
                  isOpen
                    ? "rounded-[24px] bg-white shadow-[0px_8px_24px_rgba(0,0,0,0.06)]"
                    : "rounded-[24px] bg-[#f7f7f7]"
                }
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="w-full flex items-center gap-4 lg:gap-5 px-5 lg:px-8 py-5 lg:py-6 text-left bg-transparent border-0 outline-none appearance-none rounded-[24px] cursor-pointer"
                >
                  <span className="flex items-center justify-center w-9 h-9 lg:w-11 lg:h-11 rounded-full bg-red-600 text-white text-[14px] lg:text-[16px] font-bold shrink-0">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 text-[15px] lg:text-[19px] font-semibold text-neutral-900">
                    {item.q}
                  </span>
                  <span
                    className={`shrink-0 text-neutral-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 lg:px-8 pb-6 -mt-1 pl-[64px] lg:pl-[76px]">
                    <p className="text-[14px] lg:text-[16px] text-gray-600 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};
